import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FaArrowLeft, FaSearch, FaTrash } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import depositosApi from '../../services/api/depositosApi';
import 'react-toastify/dist/ReactToastify.css';

const formatFecha = (val) => {
  if (!val) return '—';
  const s = String(val);
  return s.length >= 10 ? s.slice(0, 10).split('-').reverse().join('/') : s;
};

const formatMonto = (monto, moneda) => {
  const n = Number(monto || 0);
  return `${moneda || 'PEN'} ${n.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const nombreProveedor = (d) =>
  (d.beneficiario_nombre || d.beneficiario || '').trim() || '—';

const estadoBadge = (estado) => {
  const e = (estado || '').toLowerCase();
  if (e === 'aprobado') return 'success';
  if (e === 'cancelado') return 'secondary';
  if (e === 'registrado') return 'warning';
  return 'info';
};

const EliminarDepositos = () => {
  const navigate = useNavigate();
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [filtros, setFiltros] = useState({
    proveedor: '',
    descripcion: '',
    centro_costo_id: '',
  });
  const [aplicados, setAplicados] = useState({
    proveedor: '',
    descripcion: '',
    centro_costo_id: '',
  });
  const [depositos, setDepositos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [buscado, setBuscado] = useState(false);
  const [error, setError] = useState('');
  const [depositoAEliminar, setDepositoAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const pageSize = 50;

  useEffect(() => {
    const cargarCentros = async () => {
      try {
        const response = await depositosApi.getCentrosCosto(true);
        setCentrosCosto(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error(err);
        toast.error('No se pudieron cargar los centros de costo.');
      }
    };
    cargarCentros();
  }, []);

  const hayFiltro = (f) =>
    Boolean(
      (f.proveedor || '').trim() ||
        (f.descripcion || '').trim() ||
        f.centro_costo_id
    );

  const buscar = useCallback(
    async (pagina, f) => {
      if (!hayFiltro(f)) {
        setError('Indique proveedor, descripción o centro de costo.');
        setDepositos([]);
        setTotal(0);
        setBuscado(false);
        return;
      }
      setError('');
      setLoading(true);
      try {
        const params = { page: pagina, page_size: pageSize };
        if ((f.proveedor || '').trim()) params.proveedor = f.proveedor.trim();
        if ((f.descripcion || '').trim()) params.descripcion = f.descripcion.trim();
        if (f.centro_costo_id) params.centro_costo_id = f.centro_costo_id;
        const response = await depositosApi.getDepositos(params);
        setDepositos(response.data?.data || []);
        setTotal(response.data?.total || 0);
        setPage(pagina);
        setAplicados(f);
        setBuscado(true);
      } catch (err) {
        const d =
          err.response?.data?.detail ??
          err.response?.data?.message ??
          err.message;
        setError(typeof d === 'string' ? d : 'No se pudo buscar depósitos.');
        setDepositos([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleBuscar = (e) => {
    e?.preventDefault?.();
    buscar(1, filtros);
  };

  const confirmarEliminar = async () => {
    if (!depositoAEliminar) return;
    setEliminando(true);
    try {
      await depositosApi.deleteDeposito(depositoAEliminar.id);
      toast.success('Depósito eliminado.');
      setDepositoAEliminar(null);
      await buscar(page, aplicados);
    } catch (err) {
      const d =
        err.response?.data?.detail ??
        err.response?.data?.message ??
        'No se pudo eliminar el depósito.';
      toast.error(typeof d === 'string' ? d : 'No se pudo eliminar el depósito.');
    } finally {
      setEliminando(false);
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.75rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
          }
          .btn-dh-primary:hover { color: white; filter: brightness(1.06); }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; }
          .dh-form-label { font-weight: 600; color: #374151; }
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          .dh-alert-info {
            border-radius: 12px;
            border: none;
            background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
            color: #1e40af;
          }
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.85rem !important;
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <FaTrash size={32} />
                <div>
                  <h1 className="dh-main-title">Eliminar depósitos</h1>
                  <p className="dh-main-subtitle">
                    Busque por proveedor, descripción o centro de costo y elimine
                    el registro.
                  </p>
                </div>
              </div>
              <Button
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate('/corporativo/depositos')}
              >
                <FaArrowLeft className="me-2" /> Volver a depósitos
              </Button>
            </div>
          </Container>
        </div>

        <Container className="pb-5">
          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-header">
              <FaSearch className="me-2" /> Búsqueda
            </Card.Header>
            <Card.Body className="p-4">
              <Alert variant="info" className="dh-alert dh-alert-info">
                Use al menos un criterio. Eliminar cambia el estado a
                Eliminado: el depósito deja de verse en el listado y en los
                reportes (Excel/PDF). Puede eliminar también los aprobados o
                realizados.
              </Alert>
              <Form onSubmit={handleBuscar}>
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <Form.Label className="dh-form-label">Proveedor</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={filtros.proveedor}
                      onChange={(e) =>
                        setFiltros((prev) => ({
                          ...prev,
                          proveedor: e.target.value,
                        }))
                      }
                      placeholder="Nombre del proveedor o beneficiario"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="dh-form-label">Descripción</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={filtros.descripcion}
                      onChange={(e) =>
                        setFiltros((prev) => ({
                          ...prev,
                          descripcion: e.target.value,
                        }))
                      }
                      placeholder="Texto de la descripción"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="dh-form-label">
                      Centro de costo
                    </Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      value={filtros.centro_costo_id}
                      onChange={(e) =>
                        setFiltros((prev) => ({
                          ...prev,
                          centro_costo_id: e.target.value,
                        }))
                      }
                    >
                      <option value="">Todos</option>
                      {centrosCosto.map((cc) => (
                        <option key={cc.id} value={cc.id}>
                          {cc.codigo} — {cc.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12}>
                    <Button
                      type="submit"
                      className="btn-dh-primary d-flex align-items-center"
                      disabled={loading}
                    >
                      {loading ? (
                        <Spinner animation="border" size="sm" className="me-2" />
                      ) : (
                        <FaSearch className="me-2" />
                      )}
                      Buscar
                    </Button>
                  </Col>
                </Row>
              </Form>
              {error && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {error}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {buscado && (
            <Card className="dh-card">
              <Card.Header className="dh-card-header">
                Resultados ({total})
              </Card.Header>
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" />
                  </div>
                ) : depositos.length === 0 ? (
                  <Alert variant="warning" className="m-4">
                    No hay depósitos con esos criterios.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover className="mb-0 dh-enhanced-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Centro de costo</th>
                          <th>Proveedor</th>
                          <th>Descripción</th>
                          <th>Monto</th>
                          <th>Estado</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {depositos.map((d) => (
                            <tr key={d.id}>
                              <td>{formatFecha(d.fecha_solicitud)}</td>
                              <td>{d.centro_costo_nombre || '—'}</td>
                              <td>{nombreProveedor(d)}</td>
                              <td>{d.descripcion || '—'}</td>
                              <td>{formatMonto(d.monto, d.moneda)}</td>
                              <td>
                                <Badge bg={estadoBadge(d.estado)}>
                                  {d.estado_deposito_nombre || d.estado || '—'}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  title="Cambiar estado a Eliminado"
                                  onClick={() => setDepositoAEliminar(d)}
                                >
                                  <FaTrash className="me-1" />
                                  Eliminar
                                </Button>
                              </td>
                            </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                {total > pageSize && (
                  <div className="d-flex justify-content-between align-items-center p-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={page <= 1 || loading}
                      onClick={() => buscar(page - 1, aplicados)}
                    >
                      Anterior
                    </Button>
                    <span>
                      Página {page} de {totalPaginas}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={page >= totalPaginas || loading}
                      onClick={() => buscar(page + 1, aplicados)}
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>

      <Modal
        show={Boolean(depositoAEliminar)}
        onHide={() => !eliminando && setDepositoAEliminar(null)}
        centered
      >
        <Modal.Header closeButton={!eliminando}>
          <Modal.Title>Eliminar depósito</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {depositoAEliminar && (
            <>
              <p>
                El estado pasará a <strong>Eliminado</strong>. No aparecerá en
                el listado ni en los reportes.
              </p>
              <p className="mb-1">
                <strong>Proveedor:</strong> {nombreProveedor(depositoAEliminar)}
              </p>
              <p className="mb-1">
                <strong>Descripción:</strong>{' '}
                {depositoAEliminar.descripcion || '—'}
              </p>
              <p className="mb-0">
                <strong>Monto:</strong>{' '}
                {formatMonto(depositoAEliminar.monto, depositoAEliminar.moneda)}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={eliminando}
            onClick={() => setDepositoAEliminar(null)}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={eliminando}
            onClick={confirmarEliminar}
          >
            {eliminando ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <FaTrash className="me-2" />
            )}
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default EliminarDepositos;
